/**
 * Patient Service
 *
 * Patient API calls
 */

import { get, post } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  Patient,
  CreatePatientData,
  UpdatePatientData,
  PatientSearchFilters,
} from '@/domain/patient/types'

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

// Backend response format
interface BackendPaginatedResponse<T> {
  patients: T[]
  total: number
  limit: number
  offset: number
}

/**
 * Get paginated list of patients
 */
export async function getPatients(
  filters?: PatientSearchFilters
): Promise<PaginatedResponse<Patient>> {
  const params = new URLSearchParams()

  if (filters?.documentType) params.append('documentType', filters.documentType)
  if (filters?.documentNumber) params.append('documentNumber', filters.documentNumber)
  if (filters?.name) params.append('name', filters.name)
  if (filters?.limit) params.append('limit', String(filters.limit))
  if (filters?.offset) params.append('offset', String(filters.offset))

  const queryString = params.toString()
  const url = queryString ? `${API_ROUTES.PATIENTS.BASE}?${queryString}` : API_ROUTES.PATIENTS.BASE

  const response = await get<BackendPaginatedResponse<Patient>>(url)

  // Map backend response to frontend format
  return {
    items: response.patients,
    total: response.total,
    limit: response.limit,
    offset: response.offset,
  }
}

/**
 * Get patient by ID
 */
export async function getPatientById(id: string): Promise<Patient> {
  return get<Patient>(API_ROUTES.PATIENTS.BY_ID(id))
}

/**
 * Search patients by document
 */
export async function searchPatientsByDocument(
  documentType: string,
  documentNumber: string
): Promise<Patient[]> {
  const params = new URLSearchParams({
    documentType,
    documentNumber,
  })
  return get<Patient[]>(`${API_ROUTES.PATIENTS.SEARCH}?${params.toString()}`)
}

/**
 * Create new patient
 */
export async function createPatient(data: CreatePatientData): Promise<Patient> {
  return post<Patient>(API_ROUTES.PATIENTS.BASE, data)
}

/**
 * Update patient
 */
export async function updatePatient(id: string, data: UpdatePatientData): Promise<Patient> {
  return post<Patient>(API_ROUTES.PATIENTS.UPDATE(id), data)
}

/**
 * Delete patient (soft delete)
 */
export async function deletePatient(id: string): Promise<void> {
  return post(API_ROUTES.PATIENTS.DELETE(id), {})
}

/**
 * Get patient's studies
 * @deprecated Use usePatientStudies from @/application/studies instead
 */
export async function getPatientStudies(patientId: string): Promise<import('@/domain/study/types').Study[]> {
  const response = await get<import('@/domain/study/types').StudyListResponse>(
    API_ROUTES.PATIENTS.STUDIES(patientId)
  )
  return response.studies
}
